import { UserPermissions } from '@centecomm/portal-service';
import { useCurrentUser } from '@centecomm/portal-service-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropertyOccupantSelectionModal from 'Shared/PropertyOccupantSelection/PropertyOccupantSelectionModal';
import { LaunchDarklyFlags } from 'utils/Hooks/LaunchDarkly/launchDarklyFlags';
import useLaunchDarkly from 'utils/Hooks/LaunchDarkly/useLaunchDarkly';
import QuickActionButton from './QuickActionButton';
import SalesNavigationIcon from 'Icons/NavigationItem/Sales';

function SalesQuickActionModal(): JSX.Element | null {
    const { t } = useTranslation(['dashboardFeature']);
    const history = useHistory();
    const { isTenant } = useCurrentUser();
    const complianceHubEnabled = useLaunchDarkly(LaunchDarklyFlags.ComplianceHub);

    const [show, setShow] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [property, setProperty] = useState<number | null>(null);
    const [occupant, setOccupant] = useState<number | null>(null);
    const [hasNoProperties, setHasNoProperties] = useState<boolean>(false);

    const routeToSales = () => {
        history.push(
            isTenant
                ? `/business/${occupant}/sales`
                : complianceHubEnabled
                ? `/compliance-hub/${property}/occupants/${occupant}/sales`
                : `/locations/${property}/occupants/${occupant}/sales`,
        );
    };

    const hasNoOccupants = useMemo(() => {
        if (isTenant) {
            return t('dashboardFeature:modals.sales.errors.noBusinesses');
        }
        return t('dashboardFeature:modals.sales.errors.noOccupants');
    }, [isTenant, t]);

    useEffect(() => {
        if (show && occupant && property) routeToSales();
    }, [property, occupant, show]);

    if (hasNoProperties) return null;

    return (
        <>
            {!loading && (
                <QuickActionButton
                    Icon={SalesNavigationIcon}
                    setShow={setShow}
                    buttonText={
                        isTenant ? t('dashboardFeature:modals.sales.submit') : t('dashboardFeature:modals.sales.show')
                    }
                    strokeWidth="1px"
                />
            )}
            <PropertyOccupantSelectionModal
                show={show}
                setShow={setShow}
                modalHeader={t('dashboardFeature:modals.sales.header')}
                propertyId={property}
                occupantId={occupant}
                setPropertyId={setProperty}
                setOccupantId={setOccupant}
                setLoading={setLoading}
                setHasNoProperties={setHasNoProperties}
                noOccupantsPlaceholder={hasNoOccupants}
                userPermission={isTenant ? UserPermissions.SubmitSales : UserPermissions.ViewSales}
                occupantsPlaceholder={isTenant ? t('dashboardFeature:modals.sales.placeholder') : ''}
            />
        </>
    );
}

export default SalesQuickActionModal;
